import type { ReactNode } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import ArticleHero from '../../../components/news/ArticleHero';
import PullQuote from '../../../components/news/PullQuote';
import KeyPointsBox from '../../../components/news/KeyPointsBox';
import CommentBox from '../../../components/news/CommentBox';
import SourceCard from '../../../components/news/SourceCard';
import RelatedCards from '../../../components/news/RelatedCards';

/* ---- 記事内インライン部品（報道メディア型・帯は使わない） ---- */
function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}
function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        出典: {media}（<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>）
      </figcaption>
    </figure>
  );
}
function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}
const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function HongKongButlerAiProfitabilityPage() {
  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: 'ニュース・お知らせ', href: '/news' },
    { label: '香港Butler AI 黒字化達成' }
  ];

  const article = {
    category: 'テクノロジー・ビジネス成功事例',
    date: '2025年2月1日',
    title: '香港Butler AI 黒字化達成',
    subtitle: 'AIプラットフォームで収益3倍成長を実現',
    heroImage: '/images/generated/jba-fs-089-news_hong-kong-butler-ai-profitability.webp',
    lead: '香港を拠点とするスタートアップ「Butler」が、家事代行サービスからAIベースのプロパティ管理プラットフォームへと事業転換し、2024年第4四半期に黒字化を達成しました。収益が前年比3倍に成長し、「Butler（執事）」という概念がテクノロジービジネスとして成功している注目の事例です。',
    sections: [
      {
        heading: '企業概要とビジネスモデル転換',
        image: '/images/generated/jba-fs-090-news_hong-kong-butler-ai-profitability-v4.webp', aspect: 'aspect-[4/3]',
        alt: '大規模言語モデルを活用するAIプラットフォームのイメージ',
        paragraphs: [
          '香港を拠点とするスタートアップ「Butler」は、当初は家事代行サービスとして開始しましたが、AIベースのプロパティ管理プラットフォームへと戦略的にピボット（事業転換）を行いました。この転換により、大規模言語モデルの急速な進歩を活用する企業の潮流に乗ることができました。',
          '共同創設者Angues Chan氏によると、2024年の収益が前年比3倍に成長し、2024年第4四半期に黒字化を達成しました。さらに2025年には対応物件数が3倍になる見込みとなっています。'
        ]
      },
      {
        heading: 'AIプラットフォームのサービス内容',
        image: '/images/generated/jba-fs-091-news_hong-kong-butler-ai-profitability-v3.webp', aspect: 'aspect-[3/4]',
        alt: '不動産運営のワークフローを自動化するプロパティ管理サービスのイメージ',
        paragraphs: [
          'AIプラットフォームは、不動産管理者と家主がワークフローを監督するのを支援する包括的なソリューションを提供しています。具体的には、テナントおよび居住者の問い合わせ管理、メンテナンス作業員からの更新処理、不動産運営のワークフロー自動化などのタスクを処理します。',
          '2024年には65以上の住宅団地で使用されており、2022年のプラットフォームローンチ時の10未満の物件から大幅に拡大しています。この成長は、AI技術による効率化と顧客満足度の向上を実証しています。'
        ]
      },
      {
        heading: '香港不動産市場の背景と技術革新',
        image: '/images/generated/jba-fs-092-news_hong-kong-butler-ai-profitability-v4.webp', aspect: 'aspect-[3/4]',
        alt: '長期的な低迷期にある香港の不動産市場のイメージ',
        paragraphs: [
          '香港の不動産市場は長期的な低迷期にあり、住宅価格が継続的に下落している状況です。このような厳しい市場環境において、AI対応ソリューションが不動産管理者のコスト削減に大きく貢献しています。',
          '創設者のAngues Chan氏は「香港の不動産業界は長い間、特権的な立場にありました。今、物事がついに順調ではなくなり、変わらなければならないのです」とコメントしており、業界の変革の必要性を強調しています。'
        ]
      },
      {
        heading: '「Butler」概念のテクノロジー実現',
        image: '/images/generated/jba-fs-093-news_hong-kong-butler-ai-profitability-v3.webp', aspect: 'aspect-[4/3]',
        alt: '執事の気配りをスケーラブルに再現するデジタル執事のイメージ',
        paragraphs: [
          'この成功事例は、伝統的な業界がAI技術を活用してビジネスモデルを変革し、市場の厳しい状況下でも成長できることを示しています。「Butler」という名称を冠したこのプラットフォームは、執事サービスの概念—パーソナライズされた効率的な管理と問題解決—をテクノロジーで実現しています。',
          '従来の執事が提供していた細やかな気配りと問題解決能力を、AI技術によってスケーラブルなビジネスモデルとして展開することで、新しい価値創造を実現した画期的な事例として注目されています。'
        ]
      }
    ],
    comment: '香港Butler AIの成功は、「執事」という概念がテクノロジービジネスとして実現可能であることを証明する画期的な事例として、弊協会としても深く注目しております。執事サービスの本質であるパーソナライズされた管理と問題解決能力が、AI技術によってスケーラブルなソリューションとして展開されていることは、伝統的な執事サービスとテクノロジーの相補的関係を示す重要な示唆を含んでいます。人間の執事が提供する温かみのあるサービスとAIプラットフォームの効率性は、それぞれ異なる価値を持ちながらも、共に「執事」という概念の拡張可能性を示しています。このようなテクノロジー時代における執事概念の進化は、日本の不動産管理市場や様々な業界において、新しいサービス形態の創造につながる可能性を秘めており、私どもとしても、人間とテクノロジーが協働する次世代の執事サービスの確立に向けて、積極的に研究を進めてまいります。',
    source: [
      { label: '出典元', value: 'South China Morning Post', link: false },
      { label: '記事URL', value: 'https://www.scmp.com/tech/tech-trends/article/3297026/housekeeping-ai-hong-kong-start-butler-turns-profit-tech-pivot', link: true },
      { label: 'アクセス日', value: '2025年10月', link: false }
    ],
    related: [
      { to: '/news/ai-hospitality-revolution', image: '/images/generated/jba-fs-094-news_hong-kong-butler-ai-profitability-v4.webp', aspect: 'aspect-[3/4]', alt: 'AIホスピタリティ革命：5つの変革の関連記事', title: 'AIホスピタリティ革命：5つの変革', desc: '人工知能が切り開く執事サービスの未来' },
      { to: '/news/apple-robot-butler', image: '/images/generated/jba-fs-095-news_hong-kong-butler-ai-profitability-v4.webp', aspect: 'aspect-video', alt: 'Apple社ロボットバトラー開発プロジェクト始動の関連記事', title: 'Apple社ロボットバトラー開発プロジェクト始動', desc: '世界最大級のテクノロジー企業による執事ロボット開発' }
    ]
  };
  const S = article.sections;

  const sources = [
    { label: '出典元', value: 'South China Morning Post', link: false },
    { label: '元記事URL', value: 'https://www.scmp.com/tech/tech-trends/article/3297026/housekeeping-ai-hong-kong-start-butler-turns-profit-tech-pivot', link: true },
    { label: 'アクセス日', value: '2026年7月', link: false }
  ];
  const toc = [
    { id: 's-overview', label: '企業概要とビジネスモデル転換' },
    { id: 's-numbers', label: '数字で見る成長' },
    { id: 's-service', label: 'AIプラットフォームのサービス内容' },
    { id: 's-market', label: '香港不動産市場の背景と技術革新' },
    { id: 's-pivot', label: '不動産不況が生んだ転換' },
    { id: 's-concept', label: '「Butler」概念のテクノロジー実現' }
  ];

  return (
    <Layout>
      <ArticleHero eyebrow="News / Technology" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <p className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{article.lead}</p>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="この記事のポイント"
              points={[
                '香港のスタートアップ「Butler」が家事代行からAIプロパティ管理へ転換し、2024年第4四半期に黒字化した',
                '2024年の収益は前年比3倍、65以上の住宅団地で利用され、2025年にさらに3倍化の見込み',
                '「執事」の概念を、パーソナライズされた管理と問題解決としてAIでスケーラブルに実現した事例'
              ]}
            />

            <SectionHeading id="s-overview" en="Company">{S[0].heading}</SectionHeading>
            <div className="space-y-6">{S[0].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-numbers" en="By the Numbers">数字で見る成長</SectionHeading>
            <div className="space-y-6">
              <p className={P}>SCMPの報道によると、Butlerの2024年の収益は前年比<Stat>3倍</Stat>に成長し、<Stat>2024年第4四半期</Stat>に黒字化を達成しました。プラットフォームは2024年に65以上の住宅団地で利用されており、2022年のローンチ時の10未満から大きく拡大しています。</p>
              <p className={P}>2025年には対応物件数がさらに<Stat>3倍</Stat>になる見込みとされ、大規模言語モデルの進展を取り込んだAIプラットフォームへの転換が成長の原動力となっています（資金調達額・従業員数は非公表）。</p>
            </div>
            <SourceShot
              src="/images/news-sources/hongkong-scmp.jpg"
              alt="香港のスタートアップButlerの黒字化とテック転換を伝えるSouth China Morning Postの記事のスクリーンショット"
              media="South China Morning Post"
              url="https://www.scmp.com/tech/tech-trends/article/3297026/housekeeping-ai-hong-kong-start-butler-turns-profit-tech-pivot"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="香港の不動産業界は長い間、特権的な立場にありました。今、物事がついに順調ではなくなり、変わらなければならないのです。" cite="Angues Chan / Butler 共同創設者" />
            </div>

            <SectionHeading id="s-service" en="Service">{S[1].heading}</SectionHeading>
            <div className="space-y-6">{S[1].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-market" en="Market">{S[2].heading}</SectionHeading>
            <div className="space-y-6">{S[2].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>

            <SectionHeading id="s-pivot" en="The Pivot">不動産不況が生んだ転換</SectionHeading>
            <div className="space-y-6">
              <p className={P}>成長の背景には、香港の不動産市場の長期低迷があります。住宅価格の下落が続くなかで、不動産管理者はコスト削減を迫られており、ワークフローを自動化するAIツールの需要が高まりました。</p>
              <p className={P}>共同創設者のAngues Chan氏は業界の変革の必要性を強調しています。家事代行から不動産テック（プロパティテック）へのピボットは、伝統的な「執事」の概念をスケーラブルな事業モデルへと転換した好例といえます。</p>
            </div>
            <SourceShot
              src="/images/news-sources/hongkong-wikipedia.jpg"
              alt="出典媒体であるSouth China Morning Postの概要を解説するWikipedia英語版のページのスクリーンショット"
              media="Wikipedia（South China Morning Post）"
              url="https://en.wikipedia.org/wiki/South_China_Morning_Post"
            />

            <SectionHeading id="s-concept" en="The Concept">{S[3].heading}</SectionHeading>
            <div className="space-y-6">{S[3].paragraphs.map((p, j) => <p className={P} key={j}>{p}</p>)}</div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="目次" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>
              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">黒字化</dt><dd className="text-[#333] mt-0.5">2024年Q4</dd></div>
                  <div><dt className="text-[#8f7a56]">収益成長</dt><dd className="text-[#333] mt-0.5">前年比3倍</dd></div>
                  <div><dt className="text-[#8f7a56]">導入団地</dt><dd className="text-[#333] mt-0.5">65以上</dd></div>
                  <div><dt className="text-[#8f7a56]">出典媒体</dt><dd className="text-[#333] mt-0.5">South China Morning Post</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="全日本執事協会からのコメント" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="出典" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="関連記事" items={article.related} readLabel="詳しく" backHref="/news" backLabel="ニュース・お知らせ一覧へ戻る" />
    </Layout>
  );
}
